// apps/web/src/App.tsx

import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import EcxLoginPage from "./pages/EcxLoginPage";
import EcxCallback from "./pages/EcxCallback";
import EcxLinkPage from "./pages/EcxLinkPage";

import SelectCompanyPage from "./pages/SelectCompanyPage";
import AppShell from "./pages/AppShell";

import { UserProvider } from "./contexts/UserContext";
import AppInitializer from "./components/AppInitializer";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useUserContext } from "./contexts/UserContext";

import ResetPasswordPage from "./pages/ResetPasswordPage";
import PendingApprovalPage from "./pages/PendingApprovalPage";
import CheckStatusPage from "./pages/CheckStatusPage";
import RmsManualPage from "./pages/RmsManualPage";

import ProtectedSuperRoute from "./components/ProtectedSuperRoute";
import SuperAdminLayout from "./pages/super/SuperAdminLayout";
import SuperCompanyListPage from "./pages/super/SuperCompanyListPage";
import SuperChatworkSendersPage from "./pages/super/SuperChatworkSendersPage";
import SuperUserListPage from "./pages/super/SuperUserListPage";
import SuperNotificationsPage from "./pages/super/SuperNotificationsPage";
import SuperRakutenEventsPage from "./pages/super/SuperRakutenEventsPage";
import SuperNotificationNewPage from "./pages/super/SuperNotificationNewPage";
import SuperNotificationEditPage from "./pages/super/SuperNotificationEditPage";
import SuperSchedulerRunsPage from "./pages/super/SuperSchedulerRunsPage";
import SuperSchedulerRunDetailPage from "./pages/super/SuperSchedulerRunDetailPage";
import SuperSyncHealthPage from "./pages/super/SuperSyncHealthPage";
import SuperRmsBillingPage from "./pages/super/SuperRmsBillingPage";
import SuperRmsManualDownloadPage from "./pages/super/SuperRmsManualDownloadPage";

import SuperShopsPage from "./pages/super/SuperShopsPage";
import SuperUsersPage from "./pages/super/SuperUsersPage";
import SuperRmsProxyPage from "./pages/super/SuperRmsProxyPage";
import SuperAuthLogsPage from "./pages/super/SuperAuthLogsPage";
import SuperCompanyMonthlyMetricsPage from "./pages/super/SuperCompanyMonthlyMetricsPage";

import LoadingInline from "./components/LoadingInline";
import DemoAppInitializer from "./components/DemoAppInitializer";
import ConfigGate from "./components/ConfigGate";

// ✅ 追加（最小）：tenantパラメータでログイン先を決める
// - tenant=ecx のときだけ /ecx/login
// - tenant無し/それ以外は全部 /login（ecvision）
function pickLoginTargetByTenant(): string {
  const sp = new URLSearchParams(window.location.search);
  const tenant = (sp.get("tenant") || sp.get("t") || "").toLowerCase();
  if (tenant === "ecx") return "/ecx/login";
  return "/login";
}

// ---------------------------------------
// 🔵 認証ゲート（ログイン必須）
// - /super は常に /login（Firebase）へ
// - /ecx/* は /ecx/login へ
// - それ以外は tenant=ecx のときだけ /ecx/login、無ければ /login
// ---------------------------------------
function AuthGate({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    const auth = getAuth();

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setAuthed(false);
        setLoading(false);
        return;
      }

      try {
        await u.getIdToken();
        setAuthed(true);
      } catch {
        setAuthed(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  if (loading) return <LoadingInline />;

  if (!authed) {
    const redirect = window.location.pathname + window.location.search;
    localStorage.setItem("postLoginRedirect", redirect);

    // 既存ルールは維持
    if (redirect.startsWith("/super")) return <Navigate to="/login" replace />;
    if (redirect.startsWith("/ecx")) return <Navigate to="/ecx/login" replace />;

    // ✅ 追加（最小）：tenant=ecx のときだけ ecx/login、無ければ login
    return <Navigate to={pickLoginTargetByTenant()} replace />;
  }

  return <>{children}</>;
}

// ---------------------------------------
// 🟦 通常ユーザー向け 会社チェック
// ---------------------------------------
function CompanyGate({ children }: { children: React.ReactNode }) {
  const user = useUserContext();
  if (user.loading) return <LoadingInline />;

  if (user.error === "tenant_mismatch") {
    localStorage.removeItem("selectedCompanyId");
    localStorage.removeItem("companyId");
    localStorage.removeItem("superImpersonateCompanyId");
    localStorage.removeItem("postLoginRedirect");
    localStorage.removeItem("impTenantId");
    localStorage.removeItem("impCompanyId");
    localStorage.removeItem("impShopUrl");
    if (window.location.pathname.startsWith("/ecx")) {
      return <Navigate to="/ecx/login" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // ✅ tenant単位superも super と同等扱い
  if (
    user.superAdmin ||
    user.superAdminGlobal ||
    (Array.isArray(user.superAdminTenants) && user.superAdminTenants.length > 0)
  ) {
    return <>{children}</>;
  }

  if (user.status === "pending") return <Navigate to="/pending" replace />;
  if (!user.companyId) return <Navigate to="/check-status" replace />;

  return <>{children}</>;
}

// ---------------------------------------
// 🟣 Super: 会社を開く（companyId を入れて通常アプリへ）
// ---------------------------------------
function SuperOpenCompanyRedirect() {
  const { companyId, tenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const shopUrl = qs.get("shopUrl") || "";
    const redirectTo = qs.get("redirectTo") || "";

    if (tenantId) {
      localStorage.setItem("selectedTenant", tenantId);
      sessionStorage.setItem("login_tenant", tenantId);

      // 代理ログイン情報（あなたの apiClient が参照してる想定のキー）
      localStorage.setItem("impTenantId", tenantId);
    }

    if (companyId) {
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("selectedCompanyId", companyId);

      // 代理ログイン情報
      localStorage.setItem("impCompanyId", companyId);

      // 任意：super から開いた印（使うなら）
      localStorage.setItem("superImpersonateCompanyId", companyId);

      // ECXの accountId が残るとヘッダがブレるので消す
      localStorage.removeItem("accountId");
    }

    if (shopUrl) {
      localStorage.setItem("impShopUrl", shopUrl);
      navigate(
        `/${redirectTo ? `app/${encodeURIComponent(redirectTo)}` : "app"}?shopUrl=${encodeURIComponent(shopUrl)}&tenantId=${encodeURIComponent(
          tenantId ?? ""
        )}`,
        {
          replace: true,
        }
      );
    } else {
      navigate("/app", { replace: true });
    }
  }, [companyId, tenantId, location.search, navigate]);

  return <LoadingInline />;
}

export default function App() {
  return (
    <UserProvider>
      <AppInitializer>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />

            <Route path="/login" element={<LoginPage />} />
            <Route path="/ecx/login" element={<EcxLoginPage />} />

            <Route path="/ecx/callback" element={<EcxCallback />} />
            <Route path="/ecx/link" element={<EcxLinkPage />} />

            <Route path="/reset-done" element={<ResetPasswordPage />} />
            <Route path="/rms/manual" element={<RmsManualPage />} />

            <Route
              path="/check-status"
              element={
                <AuthGate>
                  <CheckStatusPage />
                </AuthGate>
              }
            />

            <Route
              path="/pending"
              element={
                <AuthGate>
                  <PendingApprovalPage />
                </AuthGate>
              }
            />

            <Route
              path="/select-company"
              element={
                <AuthGate>
                  <SelectCompanyPage />
                </AuthGate>
              }
            />

            {/* 通常アプリ */}
            <Route
              path="/app/*"
              element={
                <AuthGate>
                  <CompanyGate>
                    <ConfigGate>
                      <AppShell />
                    </ConfigGate>
                  </CompanyGate>
                </AuthGate>
              }
            />

            {/* デモ（認証なし） */}
            <Route
              path="/demo/*"
              element={
                <DemoAppInitializer>
                  <AppShell />
                </DemoAppInitializer>
              }
            />

            {/* ✅ SuperAdmin専用 */}
            <Route
              path="/super/*"
              element={
                <AuthGate>
                  <ProtectedSuperRoute>
                    <SuperAdminLayout />
                  </ProtectedSuperRoute>
                </AuthGate>
              }
            >
              <Route index element={<SuperCompanyListPage />} />
              <Route path="companies" element={<SuperCompanyListPage />} />
              <Route path="companies/monthly-metrics" element={<SuperCompanyMonthlyMetricsPage />} />
              <Route path="users" element={<SuperUserListPage />} />
              <Route
                path="open-company/:tenantId/:companyId"
                element={<SuperOpenCompanyRedirect />}
              />

              <Route path="chatwork-senders" element={<SuperChatworkSendersPage />} />

              <Route path="notifications" element={<SuperNotificationsPage />} />
              <Route path="rakuten-events" element={<SuperRakutenEventsPage />} />
              <Route
                path="notifications/new"
                element={<SuperNotificationNewPage />}
              />
              <Route
                path="notifications/:id"
                element={<SuperNotificationEditPage />}
              />

              <Route path="sync-health" element={<SuperSyncHealthPage />} />

              <Route
                path="scheduler-runs"
                element={<SuperSchedulerRunsPage />}
              />
              <Route
                path="scheduler-runs/:id"
                element={<SuperSchedulerRunDetailPage />}
              />

              <Route path="rms-billing" element={<SuperRmsBillingPage />} />
              <Route
                path="rms-download"
                element={<SuperRmsManualDownloadPage />}
              />
              <Route path="auth-logs" element={<SuperAuthLogsPage />} />

              <Route path="shops" element={<SuperShopsPage />} />
              <Route path="superusers" element={<SuperUsersPage />} />
              <Route path="rms-proxy" element={<SuperRmsProxyPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </AppInitializer>
    </UserProvider>
  );
}
